import { memo } from "react";
import { MapPin } from "lucide-react";
import type { BotezBokehTemplateProps } from "./types";
import { CORAL, CORAL_TEXT, CORAL_TEXT_LARGE, FONT_BODY, FONT_KICKER, FONT_SCRIPT, MUTED_TEXT, PEACHY, TEAL, TEAL_DARK, formatDate, getEventKind } from "./utils";

// JUB-154: "baptism" is a separately held baptism (wedding + baptism events).
const CEREMONY_ORDER = ["civil", "religious", "baptism", "party"] as const;

export const BotezBokehDetails = memo(({ event, config, locale, t }: BotezBokehTemplateProps) => {
  const ceremonies = CEREMONY_ORDER.map((key) => ({ key, ceremony: event.ceremonies?.[key] })).filter(
    (entry): entry is { key: (typeof CEREMONY_ORDER)[number]; ceremony: NonNullable<typeof entry.ceremony> } =>
      Boolean(entry.ceremony?.enabled),
  );

  if (ceremonies.length === 0) return null;

  // Baptism-only events get the dedicated schedule heading; mixed and
  // wedding-only events keep the neutral celebration wording.
  const { isBaptismOnly } = getEventKind(event);

  return (
    <section id="details" className="relative z-10 py-16" style={{ background: config.palette.surface }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <p
            className="text-xs tracking-[0.3em] uppercase mb-4 font-medium"
            style={{ fontFamily: FONT_KICKER, color: TEAL }}
          >
            {t("events.form.template.eventDetails")}
          </p>
          <h2
            className="text-4xl sm:text-5xl mb-4"
            style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
          >
            {isBaptismOnly
              ? t("events.form.template.baptismSchedule")
              : t("events.form.template.theCelebration")}
          </h2>
        </div>

        <div className="space-y-6">
          {ceremonies.map(({ key, ceremony }, index) => {
            const mapsUrl =
              ceremony.address || ceremony.venue
                ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                    [ceremony.address, ceremony.venue, ceremony.city].filter(Boolean).join(", "),
                  )}`
                : null;

            const dayLabel = ceremony.datetime
              ? formatDate(ceremony.datetime, locale, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : "";
            const timeLabel = ceremony.datetime
              ? formatDate(ceremony.datetime, locale, { hour: "2-digit", minute: "2-digit" })
              : "";
            const monthShort = ceremony.datetime
              ? formatDate(ceremony.datetime, locale, { month: "short" }).replace(/\.$/, "")
              : "";
            const dayNum = ceremony.datetime ? formatDate(ceremony.datetime, locale, { day: "numeric" }) : "";

            return (
              <div
                key={key}
                className="relative group motion-safe:transition-transform motion-safe:duration-300 motion-safe:hover:rotate-0 motion-safe:hover:scale-[1.02]"
                style={{ transform: `rotate(${index % 2 === 0 ? 1 : -1}deg)` }}
              >
                <div
                  className="relative bg-white p-6 md:p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-shadow duration-300 border-4"
                  style={{ borderColor: `${CORAL}33` }}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    {/* Playful organic date badge */}
                    {ceremony.datetime && (
                      <div className="flex-shrink-0 relative">
                        <div
                          className="inline-flex flex-col items-center justify-center w-24 h-24 shadow-xl relative overflow-hidden"
                          style={{
                            background: `linear-gradient(135deg, ${TEAL}, ${TEAL}cc)`,
                            borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
                            transform: "rotate(-5deg)",
                          }}
                        >
                          <div className="text-center relative z-10 text-white font-bold uppercase" style={{ fontFamily: FONT_KICKER }}>
                            <div className="text-xs leading-tight">{monthShort}</div>
                            <div className="text-xl leading-tight">{dayNum}</div>
                          </div>
                          <div className="absolute top-1 right-1 w-4 h-4 bg-white/20 rounded-full" aria-hidden="true" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-3 h-3 rounded-full" aria-hidden="true" style={{ backgroundColor: CORAL }} />
                        <div className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full" aria-hidden="true" style={{ backgroundColor: PEACHY }} />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="mb-5">
                        {dayLabel && (
                          <p
                            className="text-[10px] tracking-[0.25em] uppercase mb-3 font-medium"
                            style={{ fontFamily: FONT_KICKER, color: TEAL_DARK }}
                          >
                            {dayLabel}
                          </p>
                        )}
                        <h3
                          className="text-3xl md:text-4xl mb-3 break-words"
                          style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
                        >
                          {ceremony.name}
                        </h3>
                        <div
                          className="w-24 h-1 rounded-full"
                          aria-hidden="true"
                          style={{ background: `linear-gradient(to right, ${CORAL}, ${TEAL}, transparent)` }}
                        />
                      </div>

                      {timeLabel && (
                        <div className="mb-5">
                          <p
                            className="text-xl md:text-2xl"
                            style={{ fontFamily: FONT_BODY, color: config.palette.text }}
                          >
                            {timeLabel}
                          </p>
                        </div>
                      )}

                      {(ceremony.venue || ceremony.address || ceremony.city) && (
                        <div className="mb-6">
                          {ceremony.venue && (
                            <p
                              className="text-xl mb-1 font-medium break-words"
                              style={{ fontFamily: FONT_BODY, color: config.palette.text }}
                            >
                              {ceremony.venue}
                            </p>
                          )}
                          {(ceremony.address || ceremony.city) && (
                            <p
                              className="text-sm break-words"
                              style={{ fontFamily: FONT_BODY, color: MUTED_TEXT }}
                            >
                              {[ceremony.address, ceremony.city].filter(Boolean).join(", ")}
                            </p>
                          )}
                        </div>
                      )}

                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-xs uppercase tracking-widest motion-safe:hover:scale-105 transition-all duration-300 font-medium shadow-md"
                          style={{ fontFamily: FONT_KICKER, backgroundColor: CORAL_TEXT }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = CORAL_TEXT)}
                        >
                          <MapPin className="w-4 h-4" aria-hidden="true" />
                          {t("events.form.template.viewDirections")}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Decorative corner shapes */}
                  <div
                    className="absolute top-4 right-4 w-8 h-8 rounded-full opacity-50"
                    aria-hidden="true"
                    style={{ backgroundColor: `${PEACHY}66`, transform: "rotate(15deg)" }}
                  />
                  <div
                    className="absolute bottom-4 left-4 w-6 h-6"
                    aria-hidden="true"
                    style={{
                      backgroundColor: `${TEAL}33`,
                      borderRadius: "40% 60% 60% 40% / 60% 40% 60% 40%",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});

BotezBokehDetails.displayName = "BotezBokehDetails";
